import { Button, Tooltip } from '@atgfr8/atg-ui-tailwind';
import {
  formatRateRequestDate,
  getPendingRateRequest,
  getRateRequesterName,
  isRateRequestSubmitting,
  submitMaxRateIncreaseRequest,
} from '@store/orders/actions/rateRequestActions';
import { orderStore } from '@store/orders/store';
import { ILoadEditViewModel } from '@typeDefinitions/loadTypes';
import type { RateRequest } from '@typeDefinitions/rateRequestTypes';
import { formatAmount } from '@utils/utils';
import { For, Show } from 'solid-js';

const buttonClass = 'rounded-[4px] whitespace-nowrap shrink-0';

const MaxRateRequestTooltip = (props: {
  request: RateRequest;
  currentMaxRate: number | null | undefined;
}) => {
  return (
    <div
      class="flex flex-col gap-1 py-1 text-[13px] min-w-[280px]"
      data-testid="max-rate-request-tooltip"
    >
      <div class="pb-1 border-b border-gray-400">
        Requested by {getRateRequesterName(props.request)} on{' '}
        {formatRateRequestDate(props.request.requestedDate)}
      </div>
      <div class="text-xs uppercase text-gray-300">Requested Charges</div>
      <div class="pb-1 border-b border-gray-400">
        <For each={props.request.charges}>
          {(charge) => (
            <div class="flex justify-between gap-4">
              <span>
                {charge.type}{' '}
                <span class="text-gray-300">(Qty {charge.quantity})</span>
              </span>
              <span>{formatAmount(charge.quantity * charge.rate)}</span>
            </div>
          )}
        </For>
      </div>
      <div class="flex justify-between gap-4">
        <span>Current Max Rate</span>
        <span data-testid="max-rate-request-tooltip-current">
          {formatAmount(props.currentMaxRate ?? 0)}
        </span>
      </div>
      <div class="flex justify-between gap-4 font-semibold">
        <span>Requested Max Rate</span>
        <span data-testid="max-rate-request-tooltip-total">
          {formatAmount(props.request.maxRate)}
        </span>
      </div>
    </div>
  );
};

export const MaxRateRequestActions = (props: { load: ILoadEditViewModel }) => {
  return (
    <Show
      when={getPendingRateRequest(props.load.id)}
      fallback={
        <Button
          class={buttonClass}
          variant="outlined"
          size="sm"
          disabled={
            orderStore.isReadOnly || isRateRequestSubmitting(props.load.id)
          }
          onClick={() => void submitMaxRateIncreaseRequest(props.load)}
          dataTestId="request-max-rate-increase-button"
        >
          Request Max Rate Increase
        </Button>
      }
    >
      {(request) => (
        <Tooltip
          placement="top"
          dataTestId="max-rate-increase-requested-tooltip"
          title={
            <MaxRateRequestTooltip
              request={request()}
              currentMaxRate={props.load.maxPrice}
            />
          }
        >
          <Button
            class={`${buttonClass} pointer-events-none`}
            variant="outlined"
            size="sm"
            disabled
            dataTestId="max-rate-increase-requested-button"
          >
            Max Rate Increase Requested
          </Button>
        </Tooltip>
      )}
    </Show>
  );
};
